import {
	AbstractService,
	IFlight,
	IFlightFilterParams,
	IFlightsResponse,
} from '@move-frontend/dap';
import { resolveQueryString } from '@move-frontend/utils';
import { AxiosRequestConfig } from 'axios';
import { fromUnixTime } from 'date-fns';
import { formatDate } from '../../../../../utils/formatDate';

// NOTE: This FlightListService was created to support the old GOAA endpoint for the MCO 1.0 release
// TODO: When the new GOAA endpoint is ready this implementation should be removed

export class FlightListService extends AbstractService {
	/**
	 * Path of the resource to be used by the service.
	 */
	public static readonly URI_PATH = 'flights/get';

	/**
	 * Client version necessary for the service.
	 */
	public static readonly VERSION = '140';

	/**
	 * Request the DAP flight list.
	 * @param filter Get parameters
	 * @param requestConfig Config options for axios
	 */
	public get<FlightType extends IFlight = IFlight>(
		filter: IFlightFilterParams,
		requestConfig: AxiosRequestConfig = {},
	): Promise<IFlightsResponse<FlightType>> {
		const uri = `${FlightListService.URI_PATH}${resolveQueryString(filter)}`;

		const defaultConfig: AxiosRequestConfig = {
			method: 'POST',
			headers: { 'Api-Version': FlightListService.VERSION },
		};

		return this.client
			.request(uri, { ...defaultConfig, ...requestConfig })
			.then((resp) => {
				const ret = resp;
				// translate old GOAA endpoint data structure to new data structure
				ret.data.flights = resp.data.flight_list.map((flight: any) => ({
					id: flight.id, // same
					scheduledDate: formatDate(
						fromUnixTime(flight.scheduledTimestamp),
						'yyyy-MM-dd',
					), // construct from scheduledTimestamp (used in flightDetailURL)
					arrival: flight.arrival, // same
					codeShareFlightNumber: flight.sharedFlightNumber, // (used to display flight number in flight list)
					codeShareAirlineTrackNumber: flight.sharedSuffix,
					iataCodeShareAirline: flight.iataSharedPrefix,
					icaoCodeShareAirline: flight.icaoSharedPrefix,
					operatingAirlineFlightNumber: flight.mainFlightNumber, // (used in flight search filter)
					operatingAirlineTrackNumber: flight.mainSuffix,
					operatingAirlineSuffix: undefined,
					iataOperatingAirline: flight.iataMainPrefix,
					icaoOperatingAirline: flight.icaoMainPrefix, // (used as fallback for when there is no airlineDetail.name)
					baseAirport: 'MCO', // used in enrichment, but not more
					departureAirport: flight.originAirport, // (used to show origin airport in flight list for arrivals)
					arrivalAirport: flight.destinationAirport, // (used in flight search filter & flightDetailURL)
					viaAirport: flight.stepAirport, // (used to show destination airport in flight list for departures)
					viaSequencePosition: flight.routeStep,
					divertedAirport: undefined,
					terminal: flight.terminal, // same
					gate: flight.gate, // same
					previousGate: undefined,
					parkingStand: undefined,
					baggageHall: undefined,
					baggageBelt: [flight.baggageBelt],
					previousBaggageBelt: undefined,
					checkin: undefined,
					transferPoint: undefined,
					status: undefined,
					originalStatus: flight.status, // (used for flight status badge in flight list)
					isDelayed: undefined,
					aircraftRegistration: undefined,
					passengerHandlingAgent: undefined,
					isVisible: undefined,
					isDeleted: undefined,
					scheduledTimestamp: flight.scheduledTimestamp, // same
					estimatedTimestamp: flight.lastKnownTimestamp,
					actualTimestamp: flight.lastKnownTimestamp, // (used to compare to scheduledTimestamp in flight list)
					bestKnownTimestamp: flight.lastKnownTimestamp, // (used to check weather flight list entry is on a previous / next day)
					touchDownTimestamp: undefined,
					takeOffTimestamp: undefined,
					onBlockTimestamp: undefined,
					offBlockTimestamp: undefined,
					boardingTimestamp: undefined,
					gateClosingTimestamp: undefined,
					gateOpenTimestamp: undefined,
					onBeltTimestamp: undefined,
					remoteScheduledTimestamp: undefined,
					remoteEstimatedTimestamp: undefined,
					remoteActualTimestamp: undefined,
					remoteBestKnownTimestamp: undefined,
					lastUpdatedTimestamp: undefined,
					iataOperatingAirlineFlightNumber: flight.iataMainFlightNumber,
					icaoOperatingAirlineFlightNumber: flight.icaoMainFlightNumber,
					iataCodeShareFlightNumber: flight.iataSharedFlightNumber,
					icaoCodeShareFlightNumber: flight.icaoSharedFlightNumber,
					viaCityName: flight.stepCityName, // this property is not a part of the new API, in the future this data should be enriched through Contentful
				}));

				return ret;
			});
	}
}
